import React, { useMemo } from 'react';
import CountDown from 'react-countdown';
import styled from '@emotion/styled';
import { getDate } from '../../common';
import Container from '../../components/blocks/Container';
import Headline from '../../components/common/Headline';
import { s3BucketContentDir } from '../../config/Master';
import VerticalSeparator from '../icons/VerticalSeparator';
import breakpoints from '../../theme/material/breakpoints';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';

type PromoVariants = 'vip' | 'special' | 'intro_credit_promo';
interface PromotionalPackageContainerProps {
  countDown: any;
  isBilling?: boolean;
  promoVariant?: PromoVariants;
}

const StyledHeadline = styled(Headline)<{ promoVariant: PromoVariants }>(({ promoVariant }) => ({
  fontWeight: 700,
  color: '#fff',
  fontStyle: 'italic',
  fontSize: 26,
  paddingTop: 'unset',
  [breakpoints.down('sm')]: {
    fontSize: 17,
    paddingTop: 10,
  },
  ...(promoVariant === 'intro_credit_promo' && {
    maxWidth: 368,
    margin: 'auto',
    marginBottom: 10,
    letterSpacing: -0.22,
    lineHeight: '31px',
    fontSize: 22,
    [breakpoints.down('sm')]: {
      maxWidth: 298,
      marginBottom: 0,
      letterSpacing: '-0.17px',
      lineHeight: '24px',
      fontSize: 17,
    },
  }),
}));

const StyledTimer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: 30,
  [breakpoints.down('sm')]: {
    padding: 25,
  },
});

const StyledTimerTitle = styled('span')({
  fontSize: 56,
  fontWeight: 600,
  [breakpoints.down('sm')]: {
    fontSize: 28,
  },
});

const StyledTimerLabel = styled('span')({
  fontSize: 14,
  fontWeight: 300,
  [breakpoints.down('sm')]: {
    fontSize: 12,
  },
});

const StyledCoundown = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 20,
  [breakpoints.down('sm')]: {
    marginBottom: 15,
  },
});

const PromotionDescription = styled('div')({
  textAlign: 'center',
  padding: '10px 0',
  [breakpoints.down('sm')]: {
    padding: '5px 0 20px',
  },
});

const CountdownContainer = styled('div')({
  textAlign: 'center',
});

const SmallInfo = styled('small')<{ promoVariant: PromoVariants }>(({ promoVariant }) => ({
  display: 'block',
  paddingBottom: 25,
  paddingTop: 5,
  fontSize: 16,
  maxWidth: 'unset',
  margin: 'auto',
  fontStyle: 'italic',
  [breakpoints.down('sm')]: {
    fontSize: 15,
    maxWidth: 295,
  },
  ...(promoVariant === 'special' && {
    paddingBottom: 0,
    [breakpoints.down('sm')]: {
      paddingBottom: 10,
    },
  }),
}));

const PromoContainerBG = styled('div')({
  maxWidth: 546,
  margin: '0 auto',
  color: '#fff',
  width: '100%',
  [breakpoints.down('sm')]: {
    width: 'unset',
    maxWidth: 820,
  },
});

const Timer = ({ value, label }) => {
  return (
    <StyledTimer>
      <StyledTimerTitle>{value}</StyledTimerTitle>
      <StyledTimerLabel> {label}</StyledTimerLabel>
    </StyledTimer>
  );
};

const billingPromotionalStyle = {
  background: '#0D81E2',
  borderRadius: 10,
  marginBottom: 10,
  padding: '0 16px 0 17px',
};

const PromotionalPackageContainer = ({
  countDown,
  isBilling = false,
  promoVariant,
}: PromotionalPackageContainerProps): any => {
  const date = countDown ? getDate(countDown.date, countDown.timezone) : null;
  const { isMobile } = useMediaBreakpoints();

  const packageInfo = useMemo(() => {
    switch (promoVariant) {
      case 'vip':
        return {
          title: 'You’ve Unlocked VIP Pricing!',
          subTitle: 'Just like you, these special credit packs are too good to pass up.',
        };
      case 'special':
        return {
          title: 'Special Promo Packages Now Available!',
          subTitle: 'Take advantage before time runs out!',
        };
      case 'intro_credit_promo':
        return {
          title: 'Special Introductory Credit Pack Now Available!',
          subTitle: '',
        };
    }
  }, [promoVariant]);

  return (
    <PromoContainerBG>
      <Container
        style={
          isBilling
            ? billingPromotionalStyle
            : {
                padding: isMobile ? '0 0' : 'inherit',
              }
        }
      >
        <PromotionDescription>
          <img src={`${s3BucketContentDir}payment/promo_star.png`} width="80" alt="Promo Star" />
          <StyledHeadline promoVariant={promoVariant}>{packageInfo?.title}</StyledHeadline>
        </PromotionDescription>
        {packageInfo?.subTitle && (
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          <SmallInfo>{packageInfo?.subTitle}</SmallInfo>
        )}
        {countDown && (
          <CountdownContainer>
            <CountDown
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              date={Date.parse(date)}
              renderer={({ days, hours, minutes, seconds }) => {
                return (
                  <StyledCoundown>
                    <Timer label="Day" value={days} />
                    <VerticalSeparator />
                    <Timer label="Hrs" value={hours} />
                    <VerticalSeparator />
                    <Timer label="Min" value={minutes} />
                    <VerticalSeparator />
                    <Timer label="Sec" value={seconds} />
                  </StyledCoundown>
                );
              }}
            />
          </CountdownContainer>
        )}
      </Container>
    </PromoContainerBG>
  );
};

export default PromotionalPackageContainer;
